import * as React from 'react';
import { ISchedule } from '../../../../../common/constants';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

interface IScheduleChartProps {
  inputData: ISchedule[]
}

const ScheduleChart: React.FunctionComponent<IScheduleChartProps> = ({inputData}) => {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
  );
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: function(context: any) {
            return `Giờ: ${context[0].label}`;
          },
          label: function(context: any) {
            return `Cường độ: ${context.parsed.y}%`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'category' as const,
        title: {
          display: true,
          text: 'Giờ',
          font: {
            size: 14,
            weight: 'bold' as const
          }
        },
        ticks: {
          stepSize: 1,
          maxTicksLimit: 10,
        },
        min: 0,
        max: undefined
      },
      y: {
        title: {
          display: true,
          text: 'Cường độ (%)',
          font: {
            size: 14,
            weight: 'bold' as const
          }
        },
        min: 0,
        max: 110,
        ticks: {
          stepSize: 25,
          callback: function(value: any) {
            if (value > 100) {
              return '';
            }
            return value + '%';
          }
        }
      }
    },
    elements: {
      line: {
        tension: 0,
        stepped: 'after' as const
      },
      point: {
        radius: 6,
        backgroundColor: '#3B82F6',
        borderColor: '#FFFFFF',
        borderWidth: 2,
        hoverRadius: 8
      }
    }
  };
  
  const scheduleData = inputData
  // Get start and end times from the data
  const startTime = scheduleData[0].time;
  const endTime = scheduleData[scheduleData.length - 1].time;
  
  // Parse start and end times to get hour ranges
  const startHour = parseInt(startTime.split(':')[0]);
  const endHour = parseInt(endTime.split(':')[0]);
  
  // Handle case where end time is earlier than start time (spans midnight)
  let labels: string[] = [];
  if (endHour < startHour) {
    // Create labels from start hour to 23, then 00 to end hour
    for (let hour = startHour; hour <= 23; hour++) {
      const hourStr = hour.toString().padStart(2, '0');
      labels.push(`${hourStr}:00`);
    }
    for (let hour = 0; hour <= endHour; hour++) {
      const hourStr = hour.toString().padStart(2, '0');
      labels.push(`${hourStr}:00`);
    }
  } else {
    // Normal case: start hour to end hour
    for (let hour = startHour; hour <= endHour; hour++) {
      const hourStr = hour.toString().padStart(2, '0');
      labels.push(`${hourStr}:00`);
    }
  }
  
  // Create data array with step values based on FAKE_SCHEDULE_DATA
  const createStepData = () => {
    // Create a more granular data array for smooth visualization
    const granularData = [];
    const granularLabels = [];
    
    // console.log('Schedule data:', scheduleData);
    // console.log('Start hour:', startHour, 'End hour:', endHour);
    
    // Generate points every 5 minutes for smooth visualization
    for (let hour = startHour; hour <= (endHour < startHour ? 23 : endHour); hour++) {
      for (let minute = 0; minute < 60; minute += 5) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        granularLabels.push(timeStr);
        
        // Find the appropriate intensity value for this time
        let intensity = 0;
        const currentTimeMinutes = hour * 60 + minute;
        
        // Find the last schedule point that is before or equal to current time
        for (let i = 0; i < scheduleData.length; i++) {
          const point = scheduleData[i];
          const pointHour = parseInt(point.time.split(':')[0]);
          const pointMinute = parseInt(point.time.split(':')[1]);
          let pointTimeMinutes = pointHour * 60 + pointMinute;
          
          // Handle midnight crossing - if point is after midnight and current time is before midnight
          if (pointHour < startHour && hour >= startHour) {
            pointTimeMinutes += 24 * 60; // Add 24 hours to point time
          }
          
          if (currentTimeMinutes >= pointTimeMinutes) {
            intensity = point.value;
          }
        }
        
        granularData.push(intensity);
      }
    }
    
    // Add points for hours after midnight
    if (endHour < startHour) {
      for (let hour = 0; hour <= endHour; hour++) {
        for (let minute = 0; minute < 60; minute += 5) {
          const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          granularLabels.push(timeStr);
          
          // Find the appropriate intensity value for this time
          let intensity = 0;
          // Convert current time to continuous timeline (add 24 hours for times after midnight)
          const currentTimeMinutes = (hour + 24) * 60 + minute;
          
          // Find the last schedule point that is before or equal to current time
          for (let i = 0; i < scheduleData.length; i++) {
            const point = scheduleData[i];
            const pointHour = parseInt(point.time.split(':')[0]);
            const pointMinute = parseInt(point.time.split(':')[1]);
            let pointTimeMinutes = pointHour * 60 + pointMinute;
            
            // Convert schedule time to continuous timeline
            if (pointHour < startHour) {
              pointTimeMinutes += 24 * 60; // Add 24 hours for times after midnight
            }
            
            if (currentTimeMinutes >= pointTimeMinutes) {
              intensity = point.value;
            }
          }
          
          granularData.push(intensity);
        }
      }
    }
    
    // console.log('Granular labels:', granularLabels);
    // console.log('Granular data:', granularData);
    
    return { labels: granularLabels, data: granularData };
  };
  
  const stepData = createStepData();
  const data = {
    labels: stepData.labels, // Use granular labels
    datasets: [
      {
        label: 'Cường độ ánh sáng',
        data: stepData.data,
        borderColor: '#F59E0B',
        backgroundColor: 'rgba(245, 158, 11, 0.2)',
        borderWidth: 3,
        fill: true,
        pointBackgroundColor: 'transparent',
        // pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 8,
        pointHoverBackgroundColor: 'blue',
        stepped: 'after' as const
      }
    ],
  };
  return (
    <div>
      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Lịch trình tiết giảm ánh sáng</h3>
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div style={{ height: '300px' }}>
          <Line options={options} data={data} />
        </div>
      </div>
    </div>
  );
};

export default ScheduleChart;
